import { useCallback, useEffect, useState } from 'react';
import { fetchProfileChallenges } from '../utils/fitnessProfileChallenges';
import { mergeChallengeDayTracking } from '../utils/challengeDayTracking';
import useChallengeActions from './useChallengeActions';
import useChallengeDayChecks from './useChallengeDayChecks';

export default function useFitnessProfileChallenges({
	user,
	csrfToken,
	endpoints,
	fitnessProfileId,
	isPrimary,
	enabled
}) {
	const [challenges, setChallenges] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [actionError, setActionError] = useState('');

	const canLoad = Boolean(
		enabled &&
			user?.id &&
			fitnessProfileId &&
			endpoints?.get_user_fitness_challenges_endpoint &&
			endpoints?.get_user_fitness_profile_challenges_endpoint
	);

	const loadChallenges = useCallback(async () => {
		if (!canLoad) {
			setChallenges([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		setError('');

		try {
			const data = await fetchProfileChallenges({
				userId: user.id,
				fitnessProfileId,
				isPrimary,
				friendEndpoint: endpoints.get_user_fitness_challenges_endpoint,
				householdEndpoint: endpoints.get_user_fitness_profile_challenges_endpoint,
				csrfToken
			});
			setChallenges(data);
		} catch (e) {
			setChallenges([]);
			setError(e.message || 'Could not load challenges.');
		} finally {
			setLoading(false);
		}
	}, [canLoad, csrfToken, endpoints, fitnessProfileId, isPrimary, user?.id]);

	useEffect(() => {
		loadChallenges();
	}, [loadChallenges]);

	const { runAction } = useChallengeActions({
		user,
		csrfToken,
		endpoints,
		onAfterAction: loadChallenges
	});

	const { toggleDayCheck, isDayCheckPending } = useChallengeDayChecks({
		user,
		csrfToken,
		endpoints
	});

	const updateChallengeDayTracking = useCallback((challenge, dayTracking) => {
		setChallenges((prev) => mergeChallengeDayTracking(prev, challenge, dayTracking));
	}, []);

	const runChallengeAction = useCallback(
		async (challenge, action) => {
			setActionError('');
			try {
				await runAction(challenge, action);
			} catch (e) {
				setActionError(e.message || 'Could not update challenge.');
				throw e;
			}
		},
		[runAction]
	);

	const handleDayCheck = useCallback(
		async (challenge, isoDate, checked) => {
			setActionError('');
			try {
				await toggleDayCheck(challenge, isoDate, checked, { onUpdate: updateChallengeDayTracking });
			} catch (e) {
				setActionError(e.message || 'Could not update day tracking.');
				throw e;
			}
		},
		[toggleDayCheck, updateChallengeDayTracking]
	);

	return {
		challenges,
		loading,
		error,
		actionError,
		setActionError,
		loadChallenges,
		runAction: runChallengeAction,
		handleDayCheck,
		isDayCheckPending
	};
}
